// ╔══════════════════════════════════════════════════════════════════════════════╗
// ║                                                                              ║
// ║                      SWIFTGOLDFIDUCIARY API SERVICE                          ║
// ║                    Frontend API Integration Layer                            ║
// ║                                                                              ║
// ╚══════════════════════════════════════════════════════════════════════════════╝

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.bank.itsolution.software/api';

// ============================================================================
// DATA EXTRACTION HELPERS
// ============================================================================

/**
 * Extract array data from API response
 * Handles: direct array, {data: []}, {accounts: []}, {transactions: []}, etc.
 */
export const extractArray = (response, key = null) => {
  if (!response?.success || !response?.data) return [];

  const data = response.data;

  // Direct array
  if (Array.isArray(data)) return data;

  // Specific key provided
  if (key && Array.isArray(data[key])) return data[key];

  // Common wrapped formats
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.accounts)) return data.accounts;
  if (Array.isArray(data.transactions)) return data.transactions;
  if (Array.isArray(data.holdings)) return data.holdings;
  if (Array.isArray(data.items)) return data.items;

  return [];
};

// ============================================================================
// TOKEN MANAGEMENT
// ============================================================================

const TokenService = {
  getAccessToken: () => localStorage.getItem('accessToken'),
  getRefreshToken: () => localStorage.getItem('refreshToken'),

  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('accessToken', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  },

  clearTokens: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  },

  getUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  setUser: (user) => {
    localStorage.setItem('user', JSON.stringify(user));
  },
};

// ============================================================================
// HTTP CLIENT
// ============================================================================

class ApiClient {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add auth token if available
    const token = TokenService.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add language header for i18n
    const preferredLanguage = localStorage.getItem('preferredLanguage') || 'en';
    headers['X-Language'] = preferredLanguage;

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      // Handle token expiration or invalid session (any 401 error)
      // Skip token refresh for auth endpoints (login, register) - they should show the actual error
      const isAuthEndpoint = endpoint.includes('/auth/login') || endpoint.includes('/auth/register');

      if (response.status === 401 && !isAuthEndpoint) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          // Retry the request with new token
          headers['Authorization'] = `Bearer ${TokenService.getAccessToken()}`;
          const retryResponse = await fetch(url, { ...options, headers });
          const retryData = await retryResponse.json();
          if (!retryResponse.ok) {
            throw new ApiError(retryData.message || 'Request failed', retryResponse.status, retryData.errors);
          }
          return retryData;
        } else {
          TokenService.clearTokens();
          window.location.href = '/';
          throw new ApiError('Session expired', 401);
        }
      }

      // For auth endpoints with 401, just throw the error with the server's message
      if (response.status === 401 && isAuthEndpoint) {
        throw new ApiError(data.message || 'Invalid credentials', 401, data.errors);
      }

      if (!response.ok) {
        throw new ApiError(data.message || 'Request failed', response.status, data.errors);
      }

      return data;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError(error.message || 'Network error', 0);
    }
  }

  async refreshToken() {
    try {
      const refreshToken = TokenService.getRefreshToken();
      if (!refreshToken) return false;

      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) return false;

      const data = await response.json();
      TokenService.setTokens(data.data.accessToken);
      return true;
    } catch {
      return false;
    }
  }

  get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  post(endpoint, body = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  patch(endpoint, body = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  put(endpoint, body = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

// Custom error class
class ApiError extends Error {
  constructor(message, status, errors = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
  }
}

// Create API instance
const api = new ApiClient(API_BASE_URL);

// ============================================================================
// AUTH API
// ============================================================================

export const AuthAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    if (response.success) {
      TokenService.setTokens(response.data.tokens.accessToken, response.data.tokens.refreshToken);
      TokenService.setUser(response.data.user);
    }
    return response;
  },

  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.success) {
      TokenService.setTokens(response.data.tokens.accessToken, response.data.tokens.refreshToken);
      TokenService.setUser(response.data.user);
    }
    return response;
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      TokenService.clearTokens();
    }
  },

  getCurrentUser: () => api.get('/auth/me'),

  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),

  isAuthenticated: () => !!TokenService.getAccessToken(),

  getStoredUser: () => TokenService.getUser(),
};

// ============================================================================
// ACCOUNTS API
// ============================================================================

export const AccountsAPI = {
  getAll: () => api.get('/accounts'),
  getById: (id) => api.get(`/accounts/${id}`),
  getTypes: () => api.get('/accounts/types'),
  getBalanceHistory: (id, days = 30) => api.get(`/accounts/${id}/balance-history`, { days }),

  open: (accountTypeId, accountName, initialDeposit = 0) =>
    api.post('/accounts', { accountTypeId, accountName, initialDeposit }),

  update: (id, accountName) => api.patch(`/accounts/${id}`, { accountName }),
  close: (id) => api.delete(`/accounts/${id}`),
};

// ============================================================================
// TRANSACTIONS API
// ============================================================================

export const TransactionsAPI = {
  getAll: (params = {}) => api.get('/transactions', params),
  getById: (id) => api.get(`/transactions/${id}`),
  getCategories: () => api.get('/transactions/categories'),
  getSummary: (period = 'month', accountId = null) =>
    api.get('/transactions/summary', { period, accountId }),
  search: (query, limit = 20) => api.get('/transactions/search', { q: query, limit }),
  updateCategory: (id, categoryId) => api.patch(`/transactions/${id}/category`, { categoryId }),
};

// ============================================================================
// TRANSFERS API
// ============================================================================

export const TransfersAPI = {
  getAll: (params = {}) => api.get('/transfers', params),
  getById: (id) => api.get(`/transfers/${id}`),

  internal: (fromAccountId, toAccountId, amount, description, memo) =>
    api.post('/transfers/internal', { fromAccountId, toAccountId, amount, description, memo }),

  external: (fromAccountId, recipientId, amount, transferType, description, memo, scheduledDate) =>
    api.post('/transfers/external', { fromAccountId, recipientId, amount, transferType, description, memo, scheduledDate }),

  cancel: (id) => api.post(`/transfers/${id}/cancel`),

  // Recipients
  getRecipients: () => api.get('/transfers/recipients'),
  addRecipient: (data) => api.post('/transfers/recipients', data),
  deleteRecipient: (id) => api.delete(`/transfers/recipients/${id}`),
};

// ============================================================================
// BILLS API
// ============================================================================

export const BillsAPI = {
  getBillers: (category, search) => api.get('/bills/billers', { category, search }),
  getPayees: () => api.get('/bills/payees'),
  getPayments: (params = {}) => api.get('/bills/payments', params),
  getUpcoming: () => api.get('/bills/upcoming'),

  addPayee: (data) => api.post('/bills/payees', data),
  updatePayee: (id, data) => api.patch(`/bills/payees/${id}`, data),
  deletePayee: (id) => api.delete(`/bills/payees/${id}`),

  pay: (payeeId, accountId, amount, scheduledDate, memo) =>
    api.post('/bills/pay', { payeeId, accountId, amount, scheduledDate, memo }),

  cancelPayment: (id) => api.post(`/bills/payments/${id}/cancel`),
};

// ============================================================================
// CARDS API
// ============================================================================

export const CardsAPI = {
  getAll: () => api.get('/cards'),
  getById: (id) => api.get(`/cards/${id}`),
  getSpending: (id, period = 'month') => api.get(`/cards/${id}/spending`, { period }),

  request: (cardType, accountId, cardBrand = 'visa') =>
    api.post('/cards', { cardType, accountId, cardBrand }),

  toggleFreeze: (id) => api.post(`/cards/${id}/freeze`),
  activate: (id, lastFour) => api.post(`/cards/${id}/activate`, { lastFour }),
  report: (id, reason) => api.post(`/cards/${id}/report`, { reason }),
  updateSettings: (id, settings) => api.patch(`/cards/${id}/settings`, settings),
};

// ============================================================================
// LOANS API
// ============================================================================

export const LoansAPI = {
  getProducts: (type) => api.get('/loans/products', type ? { type } : {}),
  getApplications: (status) => api.get('/loans/applications', status ? { status } : {}),
  getLoans: (status) => api.get('/loans', status ? { status } : {}),
  getLoanById: (id) => api.get(`/loans/${id}`),

  calculate: (productId, amount, termMonths) =>
    api.post('/loans/calculate', { productId, amount, termMonths }),

  apply: (data) => api.post('/loans/apply', data),
  acceptOffer: (applicationId, disbursementAccountId) =>
    api.post(`/loans/applications/${applicationId}/accept`, { disbursementAccountId }),

  makePayment: (loanId, accountId, amount, paymentType = 'scheduled') =>
    api.post(`/loans/${loanId}/pay`, { accountId, amount, paymentType }),
};

// ============================================================================
// INVESTMENTS API
// ============================================================================

export const InvestmentsAPI = {
  getAccounts: () => api.get('/investments/accounts'),
  getAccountById: (id) => api.get(`/investments/accounts/${id}`),
  getSecurities: (params = {}) => api.get('/investments/securities', params),
  getSecurity: (symbol) => api.get(`/investments/securities/${symbol}`),
  getPriceHistory: (symbol, interval = '1m', limit = 100) =>
    api.get(`/investments/securities/${symbol}/history`, { interval, limit }),
  getOrders: (params = {}) => api.get('/investments/orders', params),
  getPortfolio: () => api.get('/investments/portfolio'),

  openAccount: (accountType, accountName, linkedAccountId) =>
    api.post('/investments/accounts', { accountType, accountName, linkedAccountId }),

  placeOrder: (investmentAccountId, securityId, orderType, side, quantity, limitPrice, stopPrice, fundingAccountId) =>
    api.post('/investments/orders', { investmentAccountId, securityId, orderType, side, quantity, limitPrice, stopPrice, fundingAccountId }),
};

// ============================================================================
// CRYPTO API
// ============================================================================

export const CryptoAPI = {
  // Wallets
  getWallets: () => api.get('/crypto/wallets'),
  getWalletById: (id) => api.get(`/crypto/wallets/${id}`),
  createWallet: (walletName, linkedAccountId) =>
    api.post('/crypto/wallets', { walletName, linkedAccountId }),

  // Currencies
  getCurrencies: (search) => api.get('/crypto/currencies', search ? { search } : {}),
  getCurrency: (symbol) => api.get(`/crypto/currencies/${symbol}`),
  getPriceHistory: (symbol, period = '1D') =>
    api.get(`/crypto/currencies/${symbol}/history`, { period }),

  // Trading
  buy: (walletId, cryptoId, amountUsd) =>
    api.post('/crypto/buy', { walletId, cryptoId, amountUsd }),
  sell: (walletId, cryptoId, quantity) =>
    api.post('/crypto/sell', { walletId, cryptoId, quantity }),

  // Portfolio & Transactions
  getTransactions: (params = {}) => api.get('/crypto/transactions', params),
  getPortfolio: () => api.get('/crypto/portfolio'),

  // Networks
  getNetworks: () => api.get('/crypto/networks'),
  getNetworksByCrypto: (cryptoId) => api.get(`/crypto/currencies/${cryptoId}/networks`),

  // Staking
  getStakingPools: () => api.get('/crypto/staking/pools'),
  getMyStakes: () => api.get('/crypto/staking/my-stakes'),
  stake: (walletId, poolId, amount) =>
    api.post('/crypto/staking/stake', { walletId, poolId, amount }),
  unstake: (stakeId, walletId) =>
    api.post('/crypto/staking/unstake', { stakeId, walletId }),
  claimStakingRewards: (stakeId, walletId) =>
    api.post('/crypto/staking/claim-rewards', { stakeId, walletId }),

  // DeFi
  getDefiPools: () => api.get('/crypto/defi/pools'),
  getMyDefiPositions: () => api.get('/crypto/defi/my-positions'),
  addLiquidity: (walletId, poolId, tokenAAmount, tokenBAmount) =>
    api.post('/crypto/defi/add-liquidity', { walletId, poolId, tokenAAmount, tokenBAmount }),
  removeLiquidity: (positionId, walletId, percentage = 100) =>
    api.post('/crypto/defi/remove-liquidity', { positionId, walletId, percentage }),

  // Deposit Addresses
  getDepositAddresses: (params = {}) => api.get('/crypto/deposit-addresses', params),
  generateDepositAddress: (walletId, cryptoId, networkId) =>
    api.post('/crypto/deposit-addresses/generate', { walletId, cryptoId, networkId }),
  getDeposits: (params = {}) => api.get('/crypto/deposits', params),

  // Withdrawals
  getWithdrawals: (params = {}) => api.get('/crypto/withdrawals', params),
  createWithdrawal: (walletId, cryptoId, networkId, amount, destinationAddress, memo) =>
    api.post('/crypto/withdraw', { walletId, cryptoId, networkId, amount, destinationAddress, memo }),
  cancelWithdrawal: (id) => api.post(`/crypto/withdrawals/${id}/cancel`),

  // Swaps
  getSwapQuote: (fromCryptoId, toCryptoId, amount) =>
    api.post('/crypto/swap/quote', { fromCryptoId, toCryptoId, amount }),
  executeSwap: (walletId, fromCryptoId, toCryptoId, amount, minReceived) =>
    api.post('/crypto/swap/execute', { walletId, fromCryptoId, toCryptoId, amount, minReceived }),
  getSwapHistory: () => api.get('/crypto/swaps'),
};

// ============================================================================
// BUDGETS & GOALS API
// ============================================================================

export const BudgetsAPI = {
  getAll: (params = {}) => api.get('/budgets', params),
  getById: (id) => api.get(`/budgets/${id}`),
  getAnalytics: (period = 'month') => api.get('/budgets/analytics', { period }),

  create: (data) => api.post('/budgets', data),
  update: (id, data) => api.patch(`/budgets/${id}`, data),
  delete: (id) => api.delete(`/budgets/${id}`),
};

export const GoalsAPI = {
  getAll: (params = {}) => api.get('/goals', params),
  getById: (id) => api.get(`/goals/${id}`),

  create: (data) => api.post('/goals', data),
  update: (id, data) => api.patch(`/goals/${id}`, data),
  contribute: (id, amount, accountId, note) =>
    api.post(`/goals/${id}/contribute`, { amount, accountId, note }),
  delete: (id) => api.delete(`/goals/${id}`),
};

// ============================================================================
// REWARDS API
// ============================================================================

export const RewardsAPI = {
  getSummary: () => api.get('/rewards'),
  getCatalog: (params = {}) => api.get('/rewards/catalog', params),
  getHistory: (type, limit = 50) => api.get('/rewards/history', { type, limit }),
  getTiers: () => api.get('/rewards/tiers'),

  redeem: (rewardId) => api.post('/rewards/redeem', { rewardId }),
};

// ============================================================================
// NOTIFICATIONS API
// ============================================================================

export const NotificationsAPI = {
  getAll: (params = {}) => api.get('/notifications', params),
  getUnreadCount: () => api.get('/notifications/unread-count'),

  markAsRead: (id) => api.post(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/read-all'),
  archive: (id) => api.post(`/notifications/${id}/archive`),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// ============================================================================
// SETTINGS API
// ============================================================================

export const SettingsAPI = {
  get: () => api.get('/settings'),
  update: (settings) => api.patch('/settings', settings),
  updateProfile: (profile) => api.patch('/settings/profile', profile),
  updateAddress: (address) => api.patch('/settings/address', address),

  getSessions: () => api.get('/settings/sessions'),
  revokeSession: (id) => api.delete(`/settings/sessions/${id}`),

  toggle2FA: (enable, code) => api.post('/settings/2fa', { enable, code }),
  getSecurityLog: (limit = 50) => api.get('/settings/security-log', { limit }),
};

// ============================================================================
// SUPPORT API
// ============================================================================

export const SupportAPI = {
  getFaqCategories: () => api.get('/support/faq/categories'),
  getFaqs: (params = {}) => api.get('/support/faq', params),
  searchFaqs: (query) => api.get('/support/faq/search', { q: query }),
  rateFaq: (id, helpful) => api.post(`/support/faq/${id}/rate`, { helpful }),

  getTickets: (params = {}) => api.get('/support/tickets', params),
  getTicketById: (id) => api.get(`/support/tickets/${id}`),
  createTicket: (data) => api.post('/support/tickets', data),
  addMessage: (ticketId, message) => api.post(`/support/tickets/${ticketId}/messages`, { message }),
  closeTicket: (id, rating, feedback) => api.post(`/support/tickets/${id}/close`, { rating, feedback }),
  reopenTicket: (id, reason) => api.post(`/support/tickets/${id}/reopen`, { reason }),

  getAnnouncements: () => api.get('/support/announcements'),
};

// ============================================================================
// NFT API
// ============================================================================

export const NFTAPI = {
  // Collections
  getCollections: (params = {}) => api.get('/nft/collections', params),
  getCollectionById: (id) => api.get(`/nft/collections/${id}`),
  getCollectionItems: (id, params = {}) => api.get(`/nft/collections/${id}/items`, params),
  createCollection: (data) => api.post('/nft/collections', data),

  // Marketplace
  getMarketplaceItems: (params = {}) => api.get('/nft/marketplace', params),

  // Items
  getItemById: (id) => api.get(`/nft/items/${id}`),
  mintItem: (data) => api.post('/nft/items', data),
  listItem: (id, price, currency = 'ETH', listingType = 'fixed', duration = null) =>
    api.post(`/nft/items/${id}/list`, { price, currency, listingType, duration }),
  updateListing: (id, price) => api.patch(`/nft/items/${id}/listing`, { price }),
  cancelListing: (id) => api.delete(`/nft/items/${id}/listing`),
  buyItem: (id) => api.post(`/nft/items/${id}/buy`),
  transferItem: (id, recipientId) => api.post(`/nft/items/${id}/transfer`, { recipientId }),

  // Offers
  makeOffer: (itemId, amount, currency = 'ETH', expirationDays = 7) =>
    api.post(`/nft/items/${itemId}/offer`, { amount, currency, expirationDays }),
  acceptOffer: (offerId) => api.post(`/nft/offers/${offerId}/accept`),
  withdrawOffer: (offerId) => api.delete(`/nft/offers/${offerId}`),
  getMyOffers: () => api.get('/nft/my/offers'),
  getReceivedOffers: () => api.get('/nft/my/received-offers'),

  // User Portfolio
  getMyNFTs: () => api.get('/nft/my/nfts'),
  getMyCreatedNFTs: () => api.get('/nft/my/created'),
  getMyCollections: () => api.get('/nft/my/collections'),
  getMyActivity: () => api.get('/nft/my/activity'),

  // Favorites
  addToFavorites: (itemId) => api.post(`/nft/items/${itemId}/favorite`),
  removeFromFavorites: (itemId) => api.delete(`/nft/items/${itemId}/favorite`),
  getFavorites: () => api.get('/nft/my/favorites'),
};

// ============================================================================
// KYC API
// ============================================================================

export const KycAPI = {
  getStatus: () => api.get('/kyc/status'),
  getLimits: () => api.get('/kyc/limits'),
  getSessions: (params = {}) => api.get('/kyc/sessions', params),
  getSessionById: (sessionId) => api.get(`/kyc/sessions/${sessionId}`),
  createSession: () => api.post('/kyc/sessions'),
  refreshSession: (sessionId) => api.post(`/kyc/sessions/${sessionId}/refresh`),
};

// ============================================================================
// ADMIN API
// ============================================================================

export const AdminAPI = {
  // Dashboard
  getDashboardStats: () => api.get('/admin/dashboard/stats'),

  // Users
  getUsers: (params = {}) => api.get('/admin/users', params),
  getUserById: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  suspendUser: (id, reason) => api.post(`/admin/users/${id}/suspend`, { reason }),
  activateUser: (id) => api.post(`/admin/users/${id}/activate`),

  // Accounts
  getAccounts: (params = {}) => api.get('/admin/accounts', params),
  adjustBalance: (id, amount, reason) => api.post(`/admin/accounts/${id}/adjust`, { amount, reason }),

  // HD Wallet Management
  getMasterWallets: () => api.get('/admin/wallets/master'),
  initializeMasterWallet: (networkCode) => api.post('/admin/wallets/master/initialize', { networkCode }),
  getExchangeSummary: () => api.get('/admin/wallets/summary'),
  getAllDepositAddresses: (params = {}) => api.get('/admin/wallets/addresses', params),
  exportPrivateKey: (addressId, reason) => api.post('/admin/wallets/export-key', { addressId, reason }),
  getWalletAuditLog: (params = {}) => api.get('/admin/wallets/audit-log', params),
  getNetworksForSetup: () => api.get('/admin/wallets/networks'),

  // Loans
  getLoans: (params = {}) => api.get('/admin/loans', params),
  approveLoan: (id) => api.post(`/admin/loans/${id}/approve`),
  rejectLoan: (id, reason) => api.post(`/admin/loans/${id}/reject`, { reason }),

  // Investments
  getInvestmentProducts: (params = {}) => api.get('/admin/investments/products', params),
  createInvestmentProduct: (data) => api.post('/admin/investments/products', data),
  updateInvestmentProduct: (id, data) => api.put(`/admin/investments/products/${id}`, data),

  // Crypto
  getCryptocurrencies: (params = {}) => api.get('/admin/crypto/currencies', params),
  updateCryptocurrency: (id, data) => api.put(`/admin/crypto/currencies/${id}`, data),

  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
};

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default {
  auth: AuthAPI,
  accounts: AccountsAPI,
  transactions: TransactionsAPI,
  transfers: TransfersAPI,
  bills: BillsAPI,
  cards: CardsAPI,
  loans: LoansAPI,
  investments: InvestmentsAPI,
  crypto: CryptoAPI,
  nft: NFTAPI,
  budgets: BudgetsAPI,
  goals: GoalsAPI,
  rewards: RewardsAPI,
  notifications: NotificationsAPI,
  settings: SettingsAPI,
  support: SupportAPI,
  kyc: KycAPI,
};

export { TokenService, ApiError };
